import type { CollectPart } from "./api";

/* Pouring a payout into the jars, one jar at a time.
 *
 * The maths lives here rather than in the component so it can be checked
 * without a browser: the thing that matters is that the numbers on the way UP
 * are always a sensible fraction of the real share and that they land on it
 * EXACTLY, because the figure the child is watching climb is the figure in
 * their jar when it stops.
 *
 * One jar at a time on purpose. All four counting up together is a blur; in
 * turn, it reads as a hand moving along the shelf, which is the thing being
 * taught -- the allowance is not one number, it is shared out.
 */

/** How long one jar takes to fill. */
export const POUR_MS = 750;
/** The beat between one jar finishing and the next starting. */
export const POUR_GAP_MS = 140;

export type PourFrame = {
  /** Cents landed in each jar so far, keyed by bucket id. */
  poured: Record<string, number>;
  /** The jar currently being filled, for the highlight. */
  activeBucketId: string | null;
  done: boolean;
};

/* Jars that get nothing are not part of the ceremony.
 *
 * A Special jar at 0% would otherwise take a full beat to receive $0.00, and
 * a pause on an empty jar reads as something having gone wrong. It still ends
 * up in `poured` at 0 so callers can treat the map as complete. */
export function pourOrder(parts: CollectPart[]): CollectPart[] {
  return parts.filter((p) => p.amountCents > 0);
}

export function pourDuration(parts: CollectPart[]): number {
  const n = pourOrder(parts).length;
  return n === 0 ? 0 : n * POUR_MS + (n - 1) * POUR_GAP_MS;
}

/* Fast at first, settling at the end -- money tipped in, not metered out. */
function ease(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Where the pour has got to, `elapsed` ms after it started. */
export function pourAt(parts: CollectPart[], elapsed: number): PourFrame {
  const order = pourOrder(parts);
  const poured: Record<string, number> = {};
  for (const p of parts) poured[p.bucketId] = 0;

  let activeBucketId: string | null = null;

  order.forEach((part, i) => {
    const start = i * (POUR_MS + POUR_GAP_MS);
    const t = (elapsed - start) / POUR_MS;

    if (t >= 1) {
      // Landed. Assigned from the part itself, never from the eased value:
      // rounding a curve at t=1 is a penny waiting to happen, and this is the
      // number that has to match the jar.
      poured[part.bucketId] = part.amountCents;
    } else if (t > 0) {
      poured[part.bucketId] = Math.round(ease(t) * part.amountCents);
      activeBucketId = part.bucketId;
    }
  });

  return { poured, activeBucketId, done: elapsed >= pourDuration(parts) };
}

/** Every jar filled, with nothing left to watch. For a reduced-motion
 *  viewer, and for the moment the animation finishes. */
export function pourComplete(parts: CollectPart[]): PourFrame {
  const poured: Record<string, number> = {};
  for (const p of parts) poured[p.bucketId] = p.amountCents;
  return { poured, activeBucketId: null, done: true };
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
